import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Trophy, ThumbsUp, ThumbsDown, HelpCircle, Sparkles, ChevronRight, CheckSquare, Square, ArrowRight } from "lucide-react";
import UnfitBadge from "@/components/UnfitBadge";

/**
 * Phase 64 — Top-voted this week widget.
 *
 * Ranks open-job applications by net vote score (interested minus
 * not_interested). Recruiters can multi-select and one-tap bulk-move all
 * picked candidates to Shortlist.
 */
export default function TopVotedWidget() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [since, setSince] = useState("week");
    const [selected, setSelected] = useState(new Set());
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/dashboard/top-voted", { params: { since, limit: 10 } });
            setItems(data?.items || []);
            setSelected(new Set());
        } catch { /* silent */ }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, [since]);
    useEffect(() => {
        const l = () => load();
        window.addEventListener("pw-feed-refresh", l);
        return () => window.removeEventListener("pw-feed-refresh", l);
    }, [since]);

    const shortlistable = useMemo(
        () => items.filter((r) => r.stage !== "Shortlist" && !r.candidate?.unfit),
        [items],
    );

    const toggle = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };
    const toggleAll = () => {
        const eligible = shortlistable.map((r) => r.application_id);
        if (eligible.every((id) => selected.has(id))) setSelected(new Set());
        else setSelected(new Set(eligible));
    };

    const bulkShortlist = async () => {
        const ids = Array.from(selected);
        if (!ids.length) return toast.error("Nothing selected");
        setBusy(true);
        try {
            const { data } = await api.post("/applications/bulk-stage", {
                application_ids: ids, stage: "Shortlist",
            });
            const n = (data?.moved || []).length;
            toast.success(`Moved ${n} candidate${n === 1 ? "" : "s"} to Shortlist`);
            setSelected(new Set());
            window.dispatchEvent(new CustomEvent("pw-feed-refresh"));
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const allSelected = shortlistable.length > 0 && shortlistable.every((r) => selected.has(r.application_id));
    const someSelected = selected.size > 0;

    return (
        <section className="border border-border bg-white" data-testid="top-voted-widget">
            <header className="px-4 py-3 border-b border-border flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 shrink-0 border border-amber-200 bg-amber-50 text-amber-700 flex items-center justify-center">
                        <Trophy className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">Leaderboard</div>
                        <div className="font-display font-bold text-base">Top-voted this {since === "day" ? "day" : since === "month" ? "month" : "week"}</div>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <div className="inline-flex border border-border text-[10px] font-mono uppercase tracking-widest">
                        {["day", "week", "month"].map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setSince(s)}
                                className={`px-2 py-1 ${since === s ? "bg-ikb text-white" : "hover:bg-secondary"}`}
                                data-testid={`top-voted-since-${s}`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
            </header>

            {loading ? (
                <div className="p-4 text-xs font-mono text-muted-foreground">Loading…</div>
            ) : items.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground" data-testid="top-voted-empty">
                    No votes cast this {since} yet. Vote on candidates in the feed to see the leaderboard fill up.
                </div>
            ) : (
                <>
                    {someSelected && (
                        <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-200 flex items-center justify-between gap-2 flex-wrap">
                            <span className="text-xs font-mono text-emerald-800">
                                {selected.size} selected
                            </span>
                            <Button
                                onClick={bulkShortlist}
                                disabled={busy}
                                className="rounded-none h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="top-voted-bulk-shortlist"
                            >
                                {busy ? "Moving…" : `Move ${selected.size} to Shortlist`}
                                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                            </Button>
                        </div>
                    )}
                    <div className="px-4 py-2 border-b border-border flex items-center gap-2">
                        <button
                            type="button"
                            onClick={toggleAll}
                            className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
                            data-testid="top-voted-select-all"
                            disabled={shortlistable.length === 0}
                        >
                            {allSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                            {allSelected ? "Clear selection" : "Select all"}
                        </button>
                        <span className="text-[10px] font-mono text-muted-foreground">
                            {shortlistable.length} eligible · {items.length - shortlistable.length} already shortlisted/unfit
                        </span>
                    </div>

                    <ul className="divide-y divide-border" data-testid="top-voted-list">
                        {items.map((r, idx) => (
                            <LeaderRow
                                key={r.application_id}
                                r={r}
                                idx={idx + 1}
                                checked={selected.has(r.application_id)}
                                disabled={r.stage === "Shortlist" || !!r.candidate?.unfit}
                                onToggle={() => toggle(r.application_id)}
                            />
                        ))}
                    </ul>
                </>
            )}
        </section>
    );
}

function LeaderRow({ r, idx, checked, disabled, onToggle }) {
    const cand = r.candidate || {};
    const job = r.job || {};
    const counts = r.vote_counts || {};
    return (
        <li className={`px-4 py-3 flex items-center gap-3 ${disabled ? "bg-secondary/40" : ""}`}
            data-testid={`top-voted-row-${r.application_id}`}>
            <button
                type="button"
                onClick={disabled ? undefined : onToggle}
                disabled={disabled}
                title={disabled ? (r.candidate?.unfit ? "Marked unfit" : "Already shortlisted") : ""}
                className="shrink-0"
                data-testid={`top-voted-checkbox-${r.application_id}`}
            >
                {disabled
                    ? <Square className="w-4 h-4 text-muted-foreground opacity-40" />
                    : checked ? <CheckSquare className="w-4 h-4 text-ikb" /> : <Square className="w-4 h-4 text-muted-foreground" />}
            </button>
            <div className="w-6 text-center font-mono text-xs font-bold text-muted-foreground shrink-0">#{idx}</div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/candidates/${cand.id}`} className="font-medium text-sm hover:text-ikb truncate">
                        {cand.name || cand.email || "Someone"}
                    </Link>
                    <UnfitBadge unfit={cand.unfit} reason={cand.unfit_reason} />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground border border-border px-1.5 py-0.5">
                        {r.stage || "Applied"}
                    </span>
                    {typeof r.match_score === "number" && (
                        <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${
                            r.match_score >= 80 ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : r.match_score >= 50 ? "border-amber-200 bg-amber-50 text-amber-700"
                            : "border-rose-200 bg-rose-50 text-rose-700"
                        }`}>
                            <Sparkles className="w-3 h-3 inline-block -mt-0.5 mr-0.5" />AI {r.match_score}
                        </span>
                    )}
                </div>
                <div className="text-xs text-muted-foreground truncate mt-0.5">
                    <Link to={`/jobs/${job.id}`} className="hover:text-ikb">{job.title || "role"}</Link>
                    {cand.current_title && <span> · {cand.current_title}</span>}
                </div>
            </div>
            <div className="flex items-center gap-2 shrink-0" title={`${counts.interested || 0} interested · ${counts.maybe || 0} maybe · ${counts.not_interested || 0} not interested`}>
                <div className="text-xs font-mono text-muted-foreground flex items-center gap-0.5">
                    <ThumbsUp className="w-3 h-3 text-emerald-600" />{counts.interested || 0}
                </div>
                <div className="text-xs font-mono text-muted-foreground flex items-center gap-0.5">
                    <HelpCircle className="w-3 h-3 text-amber-600" />{counts.maybe || 0}
                </div>
                <div className="text-xs font-mono text-muted-foreground flex items-center gap-0.5">
                    <ThumbsDown className="w-3 h-3 text-rose-600" />{counts.not_interested || 0}
                </div>
            </div>
            <div className={`px-2 py-1 border font-mono text-xs shrink-0 min-w-[52px] text-center ${
                r.net_score >= 3 ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : r.net_score >= 1 ? "border-emerald-200 bg-emerald-50/60 text-emerald-700"
                : r.net_score < 0 ? "border-rose-200 bg-rose-50 text-rose-700"
                : "border-border bg-white text-muted-foreground"
            }`} data-testid={`top-voted-net-${r.application_id}`}>
                {r.net_score > 0 ? "+" : ""}{r.net_score}
            </div>
            <Link to={`/candidates/${cand.id}`} className="shrink-0 text-muted-foreground hover:text-ikb">
                <ChevronRight className="w-4 h-4" />
            </Link>
        </li>
    );
}
